import * as Radix from "@radix-ui/themes";
import * as React from "react";
import {
  MarketplaceContexts,
  Props as MarketplaceContextsProps,
} from "~/components/MarketplaceContexts";
import * as Analytics from "~/contexts/analyticsContext";
import * as ReactRouterStubs from "~/contexts/reactRouterStubsContext";
import { useNotifications } from "~/hooks/useNotifications";
import styles from "~/root.module.css";
import * as ApiIdentitySchema from "~/routes/_marketplace.api.identity.v2/schema";

export type Props = {
  styleSheetUrl: string;
  authPromise: Promise<ApiIdentitySchema.Response>;
  segmentWriteKey?: string;
  prefabApiKey?: string;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  children?: React.ReactNode;
};

// Renders components outside of remix (ie WebFlow).
export const External: React.FC<Props> = (props) => {
  const reactRouterStubs = ReactRouterStubs.useVanillaJsReactRouterStubs();

  const cookiestring = RegExp("ajs_anonymous_id=[^;]+").exec(document.cookie);
  const cookieAnonymousId = decodeURIComponent(
    cookiestring ? cookiestring.toString().replace(/^[^=]+./, "") : "",
  );

  const [auth, setAuth] = React.useState<ApiIdentitySchema.Response>({
    ...ApiIdentitySchema.defaultResponse,
    anonymousId: cookieAnonymousId,
  });

  React.useEffect(() => {
    void props.authPromise.then(setAuth);
  }, [props.authPromise]);

  const authentication: MarketplaceContextsProps["authentication"] =
    React.useMemo(
      () => ({
        isVendrInternalEmployee: auth.isVendrInternalEmployee,
        isSyntheticTest: auth.isSyntheticTest,
        identity: auth.identity ?? null,
        platformOptedInCookie: auth.platformOptedInCookie ?? null,
        isAuthenticatedWithVendrPlatform: auth.isAuthenticatedWithVendrPlatform,
      }),
      [
        auth.isSyntheticTest,
        auth.isVendrInternalEmployee,
        auth.identity,
        auth.platformOptedInCookie,
        auth.isAuthenticatedWithVendrPlatform,
      ],
    );

  const analytics: MarketplaceContextsProps["analytics"] = React.useMemo(
    () => ({
      anonymousId: auth.anonymousId,
      analytics: props.segmentWriteKey
        ? Analytics.analytics.load({ writeKey: props.segmentWriteKey })
        : Analytics.analytics,
    }),
    [auth.anonymousId, props.segmentWriteKey],
  );

  const notifications: MarketplaceContextsProps["notifications"] =
    useNotifications({
      isLoggedIn: !!auth.identity,
    });

  const searchInputRef = React.useRef<HTMLDivElement | null>(null);
  const search: MarketplaceContextsProps["search"] = React.useMemo(
    () => ({
      appId: props.algolia?.appId ?? "",
      defaultIndex: props.algolia?.defaultIndex ?? "",
      productTierIndex: props.algolia?.productTierIndex ?? "",
      searchKey: props.algolia?.searchKey ?? "",
      searchInputRef: searchInputRef,
    }),
    [
      props.algolia?.appId,
      props.algolia?.defaultIndex,
      props.algolia?.searchKey,
      props.algolia?.productTierIndex,
    ],
  );

  const dialogContainer = React.useRef<null | HTMLDivElement>(null);
  const dialogContent = React.useRef<null | HTMLDivElement>(null);
  const dialog: MarketplaceContextsProps["dialog"] = React.useMemo(
    () => ({
      container: dialogContainer,
      content: dialogContent,
    }),
    [],
  );

  const portalContainer = React.useRef<null | HTMLDivElement>(null);
  const portal: MarketplaceContextsProps["portal"] = React.useMemo(
    () => ({
      container: portalContainer,
    }),
    [],
  );

  return (
    <Radix.Theme
      radius="small"
      accentColor="violet"
      grayColor="gray"
      className="frontend-ts-themes global-vars"
    >
      <MarketplaceContexts
        prefabApiKey={props.prefabApiKey}
        authentication={authentication}
        reactRouterStubs={reactRouterStubs}
        analytics={analytics}
        requestHeaders={null}
        notifications={notifications}
        search={search}
        dialog={dialog}
        portal={portal}
      >
        {props.children}
      </MarketplaceContexts>
      <div
        ref={dialogContainer}
        className={styles.fullPageDialogContainer}
      ></div>
      <div ref={portalContainer}></div>
    </Radix.Theme>
  );
};
